import type { SxProps, Theme } from '@mui/material';
import type { FieldDefaults } from './defaults';
import { fieldType } from './registry';
import type { Dynamic, FieldDef, FieldValues, ResolvedField } from './types';

function evaluate(setting: Dynamic<boolean> | undefined, values: FieldValues): boolean {
  return typeof setting === 'function' ? setting(values) : !!setting;
}

function joinClasses(...names: (string | undefined)[]): string | undefined {
  const joined = names.filter(Boolean).join(' ');
  return joined || undefined;
}

function stackSx(...layers: (SxProps<Theme> | undefined)[]): SxProps<Theme> | undefined {
  const present = layers.filter((l): l is SxProps<Theme> => l != null);
  if (present.length <= 1) return present[0];
  // MUI applies an array of sx layers in order, later entries winning.
  return present.flat() as SxProps<Theme>;
}

/** Merge every configuration layer for one field and work out its dynamic settings. */
export function resolveField<C>(def: FieldDef<C>, defaults: FieldDefaults, values: FieldValues): ResolvedField<C> {
  const typeLayer = (fieldType(def.type).defaults ?? {}) as Partial<FieldDef<C>>;
  const appLayer = (defaults.types?.[def.type] ?? {}) as Partial<FieldDef<C>>;
  return {
    ...typeLayer,
    ...appLayer,
    ...def,
    grid: { ...defaults.grid, ...typeLayer.grid, ...appLayer.grid, ...def.grid },
    className: joinClasses(defaults.className, typeLayer.className, appLayer.className, def.className),
    sx: stackSx(typeLayer.sx, appLayer.sx, def.sx),
    attrs: { ...typeLayer.attrs, ...appLayer.attrs, ...def.attrs },
    props: { ...typeLayer.props, ...appLayer.props, ...def.props },
    slotProps: { ...typeLayer.slotProps, ...appLayer.slotProps, ...def.slotProps },
    config: { ...typeLayer.config, ...appLayer.config, ...def.config } as C,
    disabled: evaluate(def.disabled ?? appLayer.disabled ?? typeLayer.disabled, values),
    hidden: evaluate(def.hidden ?? appLayer.hidden ?? typeLayer.hidden, values),
    size: defaults.size,
    variant: defaults.variant,
    fullWidth: defaults.fullWidth,
    shrinkLabel: defaults.shrinkLabel,
  };
}

/** `values` read at a dotted name such as `modules.form.max_file_mb`. */
export function valueAt(values: FieldValues, name: string): unknown {
  return name.split('.').reduce<unknown>(
    (node, key) => (node != null && typeof node === 'object' ? (node as FieldValues)[key] : undefined),
    values,
  );
}

/** A copy of `values` with `value` written at a dotted name. */
export function withValueAt(values: FieldValues, name: string, value: unknown): FieldValues {
  const [head, ...rest] = name.split('.');
  if (!head) return values;
  if (!rest.length) return { ...values, [head]: value };
  const child = values[head];
  return { ...values, [head]: withValueAt(child && typeof child === 'object' ? (child as FieldValues) : {}, rest.join('.'), value) };
}
